import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Send, Sparkles, Play, Pause, XCircle, Upload, Loader2, MessageSquare,
  ShieldAlert, RefreshCw, Pencil, Trash2,
} from "lucide-react";
import { fileToCsv, FILE_ACCEPT } from "@/lib/fileToCsv";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  draft:    "bg-neutral-100 text-neutral-600 border-neutral-200",
  running:  "bg-blue-50 text-blue-700 border-blue-200",
  paused:   "bg-amber-50 text-amber-700 border-amber-200",
  done:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  canceled: "bg-red-50 text-red-700 border-red-200",
};

const DELIVERY_STYLE = {
  delivered:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  sent:        "bg-sky-50 text-sky-700 border-sky-200",
  accepted:    "bg-sky-50 text-sky-700 border-sky-200",
  queued:      "bg-neutral-100 text-neutral-600 border-neutral-200",
  sending:     "bg-neutral-100 text-neutral-600 border-neutral-200",
  undelivered: "bg-red-50 text-red-700 border-red-200",
  failed:      "bg-red-50 text-red-700 border-red-200",
  unknown:     "bg-neutral-100 text-neutral-500 border-neutral-200",
};

function DeliveryPill({ s }) {
  if (!s) return null;
  return (
    <span
      className={`text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${DELIVERY_STYLE[s] || DELIVERY_STYLE.unknown}`}
      data-testid={`sms-delivery-${s}`}
    >
      {s}
    </span>
  );
}

function StatusPill({ s }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${STATUS_STYLE[s] || STATUS_STYLE.draft}`}>
      {s}
    </span>
  );
}

/**
 * Robust multi-line-aware CSV parser for SMS contacts.
 * Handles:
 *   - CSV with header row: phone,name,address (order-agnostic)
 *   - Quoted cells with embedded commas: "123 Palm Ave, Miami, FL 33101"
 *   - Plain lines: one phone per line (no header, treated as phone column)
 *   - Column aliases: phone/tel/mobile/cell, name/first_name/fullname,
 *                     address/property_address/street
 */
function _parseCsvRows(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuote) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; continue; }
      if (c === '"') { inQuote = false; continue; }
      field += c;
      continue;
    }
    if (c === '"') { inQuote = true; continue; }
    if (c === ",") { cur.push(field); field = ""; continue; }
    if (c === "\r") continue;
    if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; continue; }
    field += c;
  }
  if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((f) => (f || "").trim() !== ""));
}

// Column alias matchers — tuned for PropStream & generic skip-trace exports.
const _isPhoneHeader = (h) =>
  /(^|\b)(phone|mobile|cell|wireless|landline|tel)(\b|\s*\d)/.test(h) &&
  !/type|status|dnc|do.?not|carrier|email/.test(h);
const _isFirstName = (h) => /first.?name/.test(h);
const _isLastName  = (h) => /last.?name/.test(h);
const _isFullName  = (h) => /^name$/.test(h) || /full.?name/.test(h) || /owner.*name/.test(h) || /^owner$/.test(h);
const _isPropAddr  = (h) => /property.?address|site.?address/.test(h);
const _isAnyAddr   = (h) => /^address$|street|mailing.?address/.test(h);

/**
 * Parse pasted text or a PropStream skip-trace CSV into mass-text recipients.
 * Every phone column (Phone 1..Phone 5, Mobile, Wireless, …) is expanded into
 * its own recipient so ALL numbers on a record get texted. Deduped by digits.
 */
function parseContactsCSV(text) {
  if (!text || !text.trim()) return [];
  const rows = _parseCsvRows(text);
  if (rows.length === 0) return [];

  const firstLower = (rows[0] || []).map((h) => h.trim().toLowerCase());
  const looksLikeHeader = firstLower.some(
    (h) => _isPhoneHeader(h) || _isFullName(h) || _isFirstName(h) ||
           _isLastName(h) || _isPropAddr(h) || _isAnyAddr(h) ||
           /^(lead_id|leadid|id)$/.test(h),
  );

  const cleanCell = (v) => (v == null ? "" : String(v).trim().replace(/^"+|"+$/g, ""));
  const seen = new Set();
  const out = [];
  const pushPhone = (phone, name, address, leadId) => {
    if ((phone.match(/\d/g) || []).length < 7) return;
    if (/^(phone|tel|mobile|cell)/i.test(phone)) return; // echoed header guard
    const key = phone.replace(/\D/g, "");
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name: name || "", phone, address: address || "", lead_id: leadId || null, vars: {} });
  };

  // No header → every cell in column 0 is a phone number.
  if (!looksLikeHeader) {
    for (const cells of rows) pushPhone(cleanCell(cells[0]), "", "", null);
    return out;
  }

  const header = firstLower;
  const dataRows = rows.slice(1);
  const phoneIdxs = [];
  let firstIdx = -1, lastIdx = -1, fullIdx = -1, propIdx = -1, anyAddrIdx = -1, leadIdIdx = -1;
  header.forEach((h, i) => {
    if (_isPhoneHeader(h)) phoneIdxs.push(i);
    if (firstIdx < 0 && _isFirstName(h)) firstIdx = i;
    if (lastIdx < 0 && _isLastName(h)) lastIdx = i;
    if (fullIdx < 0 && _isFullName(h)) fullIdx = i;
    if (propIdx < 0 && _isPropAddr(h)) propIdx = i;
    if (anyAddrIdx < 0 && _isAnyAddr(h)) anyAddrIdx = i;
    if (leadIdIdx < 0 && /^(lead_id|leadid|id)$/.test(h)) leadIdIdx = i;
  });
  if (phoneIdxs.length === 0) phoneIdxs.push(0);
  const addrIdx = propIdx >= 0 ? propIdx : anyAddrIdx;

  for (const cells of dataRows) {
    let name = fullIdx >= 0 ? cleanCell(cells[fullIdx]) : "";
    if (!name && (firstIdx >= 0 || lastIdx >= 0)) {
      name = [firstIdx >= 0 ? cleanCell(cells[firstIdx]) : "",
              lastIdx  >= 0 ? cleanCell(cells[lastIdx])  : ""].filter(Boolean).join(" ");
    }
    const address = addrIdx >= 0 ? cleanCell(cells[addrIdx]) : "";
    const leadId = leadIdIdx >= 0 ? cleanCell(cells[leadIdIdx]) : null;
    for (const pi of phoneIdxs) pushPhone(cleanCell(cells[pi]), name, address, leadId);
  }
  return out;
}

function pluralize(n, s) { return `${n} ${s}${n === 1 ? "" : "s"}`; }

export default function SmsBlaster() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);   // campaign object
  const [sends, setSends] = useState([]);
  const [optOuts, setOptOuts] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const [deliveryReport, setDeliveryReport] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const { role } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/sms/campaigns");
      setCampaigns(data || []);
    } catch { /* silent */ }
    try {
      const { data } = await api.get("/sms/opt-outs");
      setOptOuts(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const loadSends = async (cid) => {
    try {
      const { data } = await api.get(`/sms/campaigns/${cid}/sends`);
      setSends(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    setDeliveryReport(null);
    if (selected) loadSends(selected.id);
  }, [selected]);

  // Refresh selected every 5s while running
  useEffect(() => {
    if (!selected || selected.status !== "running") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/sms/campaigns/${selected.id}`);
        setSelected(data);
        setCampaigns((prev) => prev.map((c) => c.id === data.id ? data : c));
        loadSends(selected.id);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [selected]);

  const startCampaign = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/sms/campaigns/${cid}/start`);
      toast.success("Campaign running");
      load(); if (selected) loadSends(selected.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
    finally { setBusy(false); }
  };
  const pauseCampaign = async (cid) => {
    try { await api.post(`/sms/campaigns/${cid}/pause`); toast.success("Paused"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Pause failed"); }
  };
  const cancelCampaign = async (cid) => {
    if (!window.confirm("Cancel this campaign? Remaining sends will be skipped.")) return;
    try { await api.post(`/sms/campaigns/${cid}/cancel`); toast.success("Canceled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const checkDelivery = async (cid) => {
    setCheckingDelivery(true);
    setDeliveryReport(null);
    try {
      const { data } = await api.post(`/sms/campaigns/${cid}/check-delivery`);
      setDeliveryReport(data);
      loadSends(cid);
      const s = data.summary || {};
      toast.success(`Delivery: ${s.delivered || 0} delivered · ${(s.undelivered || 0) + (s.failed || 0)} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delivery check failed");
    } finally { setCheckingDelivery(false); }
  };
  const deleteCampaign = async (cid) => {
    if (!window.confirm("Permanently delete this campaign and all its send records? This cannot be undone.")) return;
    try {
      await api.delete(`/sms/campaigns/${cid}`);
      toast.success("Campaign deleted");
      if (selected?.id === cid) setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed — admin only"); }
  };
  const saveEdit = async (cid, body) => {
    try {
      const { data } = await api.patch(`/sms/campaigns/${cid}`, body);
      toast.success("Campaign updated");
      setEditTarget(null);
      setSelected(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            SMS Blaster
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Bulk campaigns with spintax variation, auto opt-out handling, and
            AI-generated templates. Compliance ready.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            <ShieldAlert size={12} className="inline mr-1 text-amber-600" />
            {pluralize(optOuts.length, "opt-out")} on file
          </span>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-full h-10 px-3"
            data-testid="sms-refresh"
          >
            <RefreshCw size={13} className="mr-1" /> Refresh
          </Button>
          <Button
            onClick={() => setOpenNew(true)}
            className="rounded-full h-10 px-4 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
            data-testid="sms-new-campaign"
          >
            <Send size={13} className="mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Campaign list */}
        <div className="card-flat p-4 lg:col-span-1 space-y-1" data-testid="sms-campaign-list">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            Campaigns
          </div>
          {campaigns.length === 0 && (
            <div className="text-xs text-muted-foreground py-4">
              No campaigns yet. Create one to start blasting.
            </div>
          )}
          {campaigns.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c)}
              className={`w-full text-left rounded-lg px-3 py-2 border transition-colors ${
                selected?.id === c.id
                  ? "border-[var(--app-black)] bg-[var(--app-surface)]"
                  : "border-transparent hover:bg-[var(--app-surface)]"
              }`}
              data-testid={`sms-camp-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <StatusPill s={c.status} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {c.stats?.sent || 0}/{c.stats?.total || 0} sent
                {c.stats?.skipped ? ` · ${c.stats.skipped} skipped` : ""}
                {c.stats?.failed ? ` · ${c.stats.failed} failed` : ""}
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-3">
          {selected ? (
            <>
            <CampaignDetail
              campaign={selected}
              sends={sends}
              busy={busy}
              onStart={startCampaign}
              onPause={pauseCampaign}
              onCancel={cancelCampaign}
              onCheckDelivery={checkDelivery}
              checkingDelivery={checkingDelivery}
              deliveryReport={deliveryReport}
              onEdit={(c) => setEditTarget(c)}
              onDelete={deleteCampaign}
              canDelete={role === "admin"}
            />
            {editTarget && (
              <EditCampaignDialog
                campaign={editTarget}
                onClose={() => setEditTarget(null)}
                onSave={saveEdit}
              />
            )}
            </>
          ) : (
            <div className="card-flat p-6 text-center text-sm text-muted-foreground">
              Select a campaign to see its progress and message-by-message
              delivery status.
            </div>
          )}
        </div>
      </div>

      <NewCampaignDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onCreated={(c) => {
          setOpenNew(false);
          setCampaigns((prev) => [c, ...prev]);
          setSelected(c);
        }}
      />
    </div>
  );
}

function CampaignDetail({ campaign, sends, busy, onStart, onPause, onCancel, onCheckDelivery, checkingDelivery, deliveryReport, onEdit, onDelete, canDelete }) {
  const s = campaign.stats || {};
  return (
    <>
      <div className="card-flat p-5" data-testid="sms-detail">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display text-lg font-bold">{campaign.name}</div>
              <StatusPill s={campaign.status} />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
              Rate {campaign.send_rate_per_min || 30}/min · Created {new Date(campaign.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex gap-2">
            {onCheckDelivery && (
              <Button
                variant="outline"
                onClick={() => onCheckDelivery(campaign.id)}
                disabled={checkingDelivery}
                className="rounded-full"
                data-testid="sms-check-delivery"
              >
                {checkingDelivery
                  ? <Loader2 size={13} className="mr-2 animate-spin" />
                  : <RefreshCw size={13} className="mr-2" />}
                Check delivery
              </Button>
            )}
            {["draft", "paused"].includes(campaign.status) && onEdit && (
              <Button variant="outline" onClick={() => onEdit(campaign)} className="rounded-full" data-testid="sms-edit">
                <Pencil size={13} className="mr-2" /> Edit
              </Button>
            )}
            {campaign.status === "draft" && (
              <Button
                onClick={() => onStart(campaign.id)}
                disabled={busy}
                className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                data-testid="sms-start"
              >
                <Play size={13} className="mr-2" /> Launch
              </Button>
            )}
            {campaign.status === "paused" && (
              <Button
                onClick={() => onStart(campaign.id)}
                disabled={busy}
                className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                data-testid="sms-resume"
              >
                <Play size={13} className="mr-2" /> Resume
              </Button>
            )}
            {campaign.status === "running" && (
              <Button variant="outline" onClick={() => onPause(campaign.id)} data-testid="sms-pause">
                <Pause size={13} className="mr-2" /> Pause
              </Button>
            )}
            {["draft", "running", "paused"].includes(campaign.status) && (
              <Button variant="outline" onClick={() => onCancel(campaign.id)} data-testid="sms-cancel">
                <XCircle size={13} className="mr-2" /> Cancel
              </Button>
            )}
            {canDelete && onDelete && (
              <Button
                variant="outline"
                onClick={() => onDelete(campaign.id)}
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                data-testid="sms-delete"
              >
                <Trash2 size={13} className="mr-2" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
          {["total", "sent", "pending", "skipped", "failed"].map((k) => (
            <div key={k} className="rounded-lg border border-[var(--app-line)] p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
              <div className="text-xl font-display font-bold" data-testid={`sms-stat-${k}`}>
                {s[k] ?? 0}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Template</div>
          <div className="text-sm bg-[var(--app-surface)] rounded-md p-3 whitespace-pre-wrap">
            {campaign.template}
          </div>
        </div>
      </div>

      {deliveryReport && (
        <div className="card-flat p-5" data-testid="sms-delivery-report">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            Carrier delivery{deliveryReport.checked > 0 ? ` · ${deliveryReport.checked} checked` : ""}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {["delivered", "sent", "queued", "undelivered", "failed"].map((k) => (
              <div key={k} className="rounded-lg border border-[var(--app-line)] p-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                <div
                  className={`text-xl font-display font-bold ${
                    k === "delivered" ? "text-emerald-600"
                      : (k === "undelivered" || k === "failed") ? "text-red-600" : ""}`}
                  data-testid={`sms-delivery-stat-${k}`}
                >
                  {deliveryReport.summary?.[k] ?? 0}
                </div>
              </div>
            ))}
          </div>
          {deliveryReport.not_sent > 0 && (
            <div className="text-[11px] text-muted-foreground mt-2">
              {deliveryReport.not_sent} never left the server (skipped / opted-out — no carrier status).
            </div>
          )}
          {deliveryReport.reasons?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Why messages didn't land
              </div>
              <ul className="space-y-1">
                {deliveryReport.reasons.map((r, i) => (
                  <li key={i}
                      className="text-xs flex items-center justify-between gap-2 border-b border-[var(--app-line)] last:border-b-0 py-1">
                    <span className="text-amber-800">{r.reason}</span>
                    <span className="font-mono text-muted-foreground">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="card-flat p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Recent Messages
          </div>
          <a
            href="/sms-inbox"
            className="text-[11px] font-medium text-[var(--app-accent-dark)] hover:underline flex items-center gap-1"
            data-testid="sms-view-replies-link"
          >
            <MessageSquare size={12} /> View replies in Inbox →
          </a>
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1" data-testid="sms-sends">
          {sends.length === 0 ? (
            <div className="text-xs text-muted-foreground py-3">
              No messages sent yet.
            </div>
          ) : (
            sends.slice(0, 200).map((r) => (
              <div key={r.id}
                   className="flex items-start gap-3 text-xs border-b border-[var(--app-line)] last:border-b-0 py-1.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-[var(--app-surface)] shrink-0">
                  <MessageSquare size={11} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{r.contact_name || "—"}</span>
                    <span className="font-mono text-muted-foreground">{r.contact_phone}</span>
                    <StatusPill s={r.status} />
                    <DeliveryPill s={r.delivery_status} />
                  </div>
                  {r.rendered_body && (
                    <div className="text-neutral-600 mt-0.5 truncate">{r.rendered_body}</div>
                  )}
                  {r.error && <div className="text-red-600 mt-0.5">{r.error}</div>}
                  {r.delivery_error_reason && (r.delivery_status === "undelivered" || r.delivery_status === "failed") && (
                    <div className="text-amber-700 mt-0.5">{r.delivery_error_reason}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

function NewCampaignDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [template, setTemplate] = useState(
    "{Hey|Hi} {name}, {saw|noticed} your place at {address}. Any interest in a cash offer? — Arturo"
  );
  const [csv, setCsv] = useState("");
  const [rate, setRate] = useState(30);
  const [appendOptOut, setAppendOptOut] = useState(true);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiVariants, setAiVariants] = useState([]);
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);
  // AI Blast mode — when ON we bypass the static template and route each
  // contact through Claude for a personalized first-touch. Requires the
  // uploaded CSV to include lead_id (so we can attach the AI conversation).
  const [aiBlast, setAiBlast] = useState(false);
  const [aiCampaignHint, setAiCampaignHint] = useState(
    "cash offer on their property, low-pressure opener"
  );
  // Recipient source + full-speed sending
  const [source, setSource] = useState("upload");   // upload | list
  const [lists, setLists] = useState([]);
  const [listName, setListName] = useState("");
  const [listContacts, setListContacts] = useState([]);
  const [listBusy, setListBusy] = useState(false);
  const [noLimit, setNoLimit] = useState(true);

  const contacts = useMemo(
    () => (source === "list" ? listContacts : parseContactsCSV(csv)),
    [source, listContacts, csv],
  );

  useEffect(() => {
    if (!open) return;
    api.get("/leads/lists")
       .then((r) => setLists(r.data?.lists || []))
       .catch(() => setLists([]));
  }, [open]);

  const loadList = async (name) => {
    setListName(name);
    if (!name) { setListContacts([]); return; }
    setListBusy(true);
    try {
      const { data } = await api.get("/sms/list-contacts", { params: { list_name: name } });
      setListContacts(data.contacts || []);
      toast.success(`${data.count} numbers loaded from "${name}"`);
    } catch { toast.error("Couldn't load that list"); }
    finally { setListBusy(false); }
  };

  useEffect(() => {
    if (!open) return;
    // Live preview against first contact
    const first = contacts[0] || { name: "John Doe", address: "123 Palm Ave", phone: "+13055550001" };
    api.post("/sms/preview", { template, contact: first, append_opt_out: appendOptOut })
       .then((r) => setPreview(r.data.variants || []))
       .catch(() => setPreview([]));
  }, [template, contacts, appendOptOut, open]);

  const askAi = async () => {
    if (!aiTopic.trim()) return toast.error("What's this campaign about?");
    setAiBusy(true);
    try {
      const { data } = await api.post("/sms/ai/generate-templates",
        { topic: aiTopic, tone: "warm" });
      setAiVariants(data.templates || []);
      if ((data.templates || []).length === 0) toast.error("AI returned no templates");
    } catch { toast.error("AI failed"); }
    finally { setAiBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setCsv(await fileToCsv(f));
      toast.success(`Loaded ${f.name}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't read the file");
    }
    e.target.value = "";
  };

  const create = async () => {
    if (contacts.length === 0) return toast.error("Add at least one contact");
    setBusy(true);
    try {
      if (aiBlast) {
        // Route through the AI kickoff endpoint. It expects lead_ids, so
        // contacts MUST already be in the CRM (have `lead_id`). We collect
        // whichever ones qualify and skip the rest with a warning.
        const withLeadId = contacts
          .map((c) => c.lead_id || c.leadId || null)
          .filter(Boolean);
        if (withLeadId.length === 0) {
          toast.error("AI Blast needs leads that already exist in the CRM. Include a `lead_id` column in your CSV or run this from the CRM board instead.");
          setBusy(false);
          return;
        }
        const { data } = await api.post("/ai-sms/kickoff-bulk", {
          lead_ids: withLeadId,
          campaign_hint: aiCampaignHint,
          name: name.trim() || "AI Blast",
          delay_ms: noLimit ? 0 : Math.max(500, Math.round(60000 / (Number(rate) || 30))),
        });
        onCreated(data.campaign || { name: name.trim() || "AI Blast", ai_blast: true });
        toast.success(`AI Blast started — ${data.attempted} recipients queued. Watch progress here.`);
      } else {
        if (!template.trim()) return toast.error("Template required");
        const { data } = await api.post("/sms/campaigns", {
          name: name.trim() || `Campaign ${new Date().toLocaleDateString()}`,
          template,
          contacts,
          send_rate_per_min: noLimit ? 100000 : (Number(rate) || 30),
          append_opt_out: appendOptOut,
        });
        onCreated(data);
        toast.success(`Created — ${contacts.length} recipients queued`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl" data-testid="sms-new-dialog">
        <DialogHeader>
          <DialogTitle>New SMS Campaign</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Spintax like <code className="text-[10px]">{`{Hey|Hi}`}</code> randomizes per contact. Merges like <code className="text-[10px]">{`{name}`}</code> or <code className="text-[10px]">{`{address}`}</code> substitute per row.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Campaign name</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Q1 FSBO Blast" data-testid="sms-new-name" />
            </div>

            {/* AI Blast toggle — swaps the static template for AI-personalized openers */}
            <div className={`rounded-xl border p-3 transition-colors ${
              aiBlast ? "border-violet-300 bg-violet-50/60" : "border-[var(--app-line)] bg-[var(--app-surface)]"}`}
                 data-testid="ai-blast-toggle">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-violet-600"
                       checked={aiBlast}
                       onChange={(e) => setAiBlast(e.target.checked)}
                       data-testid="ai-blast-checkbox" />
                <span className="text-xs font-semibold uppercase tracking-widest text-violet-700">
                  AI Blast — Claude personalizes each opener
                </span>
              </label>
              <div className="text-[10px] text-muted-foreground mt-1 ml-6">
                Each recipient gets a one-off first-touch. When they reply the AI keeps qualifying them until they ask to be called.
              </div>
              {aiBlast && (
                <div className="mt-2 ml-6">
                  <Label className="text-[10px] uppercase tracking-widest text-violet-700">
                    Campaign angle (optional hint to the AI)
                  </Label>
                  <Input
                    className="mt-1 h-8 text-xs font-mono"
                    value={aiCampaignHint}
                    onChange={(e) => setAiCampaignHint(e.target.value)}
                    placeholder="e.g. quick cash close for Cape Coral flood-zone properties"
                    data-testid="ai-blast-hint"
                  />
                  <div className="text-[10px] text-amber-700 mt-1.5">
                    Requires each contact to already exist in the CRM (include a `lead_id` column in your CSV).
                  </div>
                </div>
              )}
            </div>

            {!aiBlast && (
              <div>
                <Label className="text-xs">Message template</Label>
                <Textarea rows={5} className="mt-1 font-mono text-xs" value={template}
                          onChange={(e) => setTemplate(e.target.value)}
                          data-testid="sms-new-template" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Char count: {template.length} · 160 = 1 segment
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-1 mb-2" data-testid="sms-source-tabs">
                <button type="button" onClick={() => setSource("upload")}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${source === "upload" ? "bg-[var(--app-black)] text-white border-[var(--app-black)]" : "border-[var(--app-line)] text-muted-foreground hover:bg-[var(--app-surface)]"}`}
                  data-testid="sms-source-upload">Upload / Paste</button>
                <button type="button" onClick={() => setSource("list")}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${source === "list" ? "bg-[var(--app-black)] text-white border-[var(--app-black)]" : "border-[var(--app-line)] text-muted-foreground hover:bg-[var(--app-surface)]"}`}
                  data-testid="sms-source-list">Existing list</button>
              </div>

              {source === "upload" ? (
                <>
                  <Label className="text-xs">PropStream CSV — every phone column gets texted</Label>
                  <Textarea rows={5} className="mt-1 font-mono text-xs" value={csv}
                            onChange={(e) => setCsv(e.target.value)}
                            placeholder={"Paste phones one per line, or drop a PropStream skip-trace CSV with Phone 1, Phone 2, Owner First Name, Property Address…"}
                            data-testid="sms-new-csv" />
                  <label className="mt-1 inline-flex items-center text-[10px] text-muted-foreground cursor-pointer hover:underline">
                    <Upload size={10} className="mr-1" />
                    Upload CSV / XLSX file
                    <input type="file" accept={FILE_ACCEPT} onChange={onFile} className="hidden"
                           data-testid="sms-new-csv-file" />
                  </label>
                </>
              ) : (
                <>
                  <Label className="text-xs">Pick an imported list</Label>
                  <select
                    className="mt-1 w-full h-9 rounded-md border border-[var(--app-line)] bg-white px-2 text-xs"
                    value={listName}
                    onChange={(e) => loadList(e.target.value)}
                    data-testid="sms-source-list-select"
                  >
                    <option value="">— select a list —</option>
                    {lists.map((l) => (
                      <option key={l.name} value={l.name}>
                        {l.name} ({l.with_phone} with phone)
                      </option>
                    ))}
                  </select>
                  {listBusy && (
                    <div className="text-[10px] text-muted-foreground mt-1">
                      <Loader2 size={10} className="inline animate-spin mr-1" /> Loading numbers…
                    </div>
                  )}
                </>
              )}

              <div
                className={`text-[11px] mt-1 ${
                  contacts.length > 0 ? "text-emerald-700 font-medium" : "text-muted-foreground"
                }`}
                data-testid="sms-new-count"
              >
                {pluralize(contacts.length, "recipient")} ready
                {source === "upload" && contacts.length === 0 && csv.trim() && (
                  <span className="text-red-600 ml-1">— check the format, needs a phone column</span>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--app-line)] p-2.5">
                <div>
                  <div className="text-xs font-medium">Send as fast as possible</div>
                  <div className="text-[10px] text-muted-foreground">No rate cap — blast at full speed</div>
                </div>
                <Switch checked={noLimit} onCheckedChange={setNoLimit} data-testid="sms-new-nolimit" />
              </div>
              {!noLimit && (
                <div>
                  <Label className="text-xs">Rate (per min)</Label>
                  <Input type="number" min="1" className="mt-1" value={rate}
                         onChange={(e) => setRate(e.target.value)}
                         data-testid="sms-new-rate" />
                </div>
              )}
              <div className="flex items-end justify-between gap-2">
                <div>
                  <div className="text-xs">Auto append STOP</div>
                  <div className="text-[10px] text-muted-foreground">CTIA-required opt-out</div>
                </div>
                <Switch checked={appendOptOut} onCheckedChange={setAppendOptOut} data-testid="sms-new-optout" />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border border-[var(--app-line)] p-3 bg-[var(--app-surface)]">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                Live preview (3 spins)
              </div>
              <div className="space-y-1.5">
                {preview.length === 0 ? (
                  <div className="text-xs text-muted-foreground">Type a template…</div>
                ) : (
                  preview.map((v, i) => (
                    <div key={i} className="text-xs bg-white rounded p-2 border border-[var(--app-line)]" data-testid={`sms-new-preview-${i}`}>
                      {v}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-lg border border-[var(--app-line)] p-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                <Sparkles size={11} className="inline mr-1" /> AI template writer
              </div>
              <div className="flex gap-1">
                <Input placeholder="e.g. Follow-up after voicemail" value={aiTopic}
                       onChange={(e) => setAiTopic(e.target.value)}
                       data-testid="sms-new-ai-topic" />
                <Button variant="outline" onClick={askAi} disabled={aiBusy} data-testid="sms-new-ai-btn">
                  {aiBusy ? <Loader2 size={12} className="animate-spin" /> : "Ask AI"}
                </Button>
              </div>
              {aiVariants.length > 0 && (
                <div className="space-y-1 mt-2">
                  {aiVariants.map((v, i) => (
                    <button
                      key={i}
                      onClick={() => setTemplate(v)}
                      className="w-full text-left text-xs bg-[var(--app-surface)] hover:bg-white border border-[var(--app-line)] rounded p-2"
                      data-testid={`sms-new-ai-variant-${i}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={create}
            disabled={busy}
            className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
            data-testid="sms-new-create"
          >
            {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Send size={13} className="mr-2" />}
            Create Campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditCampaignDialog({ campaign, onClose, onSave }) {
  const [name, setName] = useState(campaign.name || "");
  const [template, setTemplate] = useState(campaign.template || "");
  const [rate, setRate] = useState(campaign.send_rate_per_min || 30);
  const [optOut, setOptOut] = useState(campaign.append_opt_out !== false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    await onSave(campaign.id, {
      name,
      template,
      send_rate_per_min: Number(rate) || 30,
      append_opt_out: optOut,
    });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sms-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit campaign</DialogTitle>
          <DialogDescription>
            Changes apply to every message not yet sent. Editable while the
            campaign is a draft or paused.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="sms-edit-name" />
          </div>
          <div>
            <Label>Message template</Label>
            <Textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={5} className="mt-1" data-testid="sms-edit-template" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Use {"{name}"}, {"{address}"} and spintax like {"{Hi|Hello}"}.
            </p>
          </div>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>Send rate (per min)</Label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1" data-testid="sms-edit-rate" />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch checked={optOut} onCheckedChange={setOptOut} data-testid="sms-edit-optout" />
              <span className="text-sm">Append opt-out</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="sms-edit-save">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
